import * as React from 'react';
import { FC, useState } from 'react';

import { Box, Checkbox } from '@rbilabs/components-library';
import { Formik, FormikProps } from 'formik';

import ActionButton from 'components/action-button';
import List, { ListItem } from 'components/list';
import LoadingAnimation from 'components/loading-animation';
import { ALERT_STYLE, SupportAlertBox } from 'components/support-alert-box';
import { FormStatus } from 'enums/support-form';
import useMediaQuery from 'hooks/use-media-query';
import { PageHeading, PageWrap, StyledBackButton } from 'pages/report-order-issue/styled';
import noop from 'utils/noop';

import {
  CenteredP,
  CommentTextareaInput,
  ItemNameText,
  LeftMiddleFlex,
  MissingItemNumberInput,
  Spacing,
} from './styled';
import {
  IReportOrderItemsFormData,
  IReportOrderItemsItem,
  IReportOrderItemsItemRefund,
  IReportOrderItemsMissingPageViewProps,
} from './types';
import { isItemOrChildSelected } from './util';

/** id based search and update on items including children */
const deepUpdate = (
  list: IReportOrderItemsItemRefund[],
  currentItem: IReportOrderItemsItemRefund,
  value: Partial<IReportOrderItemsItemRefund>
) =>
  list.map(
    (current): IReportOrderItemsItemRefund => {
      if (current.id === currentItem.id) {
        return {
          ...current,
          // including children condition check because of bad data structure for past orders
          ...(current.children
            ? { children: deepUpdate(current.children || [], currentItem, value) }
            : value),
        };
      }
      return {
        ...current,
        // only include children if they exist
        ...(current.children && {
          children: deepUpdate(current.children || [], currentItem, value),
        }),
      };
    }
  );

/**
 * Used to convert order items into formik values
 */
const mapItemsToRefundItems = (items?: IReportOrderItemsItem[]) =>
  items?.map(
    ({ id, name, quantity, children }): IReportOrderItemsItemRefund => ({
      id,
      name,
      quantity,
      refundCount: 0,
      children: mapItemsToRefundItems(children),
    })
  );

export const ReportOrderItemsMissingPageView: FC<IReportOrderItemsMissingPageViewProps> = ({
  headingText = 'Heading',
  subheadingText = 'Subheading',
  contentText = 'Content',
  commentsPlaceholderText = 'Comments',
  submitButtonText = 'Submit',
  refundErrorText = 'failed',
  refundSuccessText = 'success',
  isLoading = false,
  items = [],
  formStatus,
  onBack = noop,
  onSubmit = noop,
  disableSubmitButton,
}) => {
  const [isNothingSelected, setIsNothingSelected] = useState(true);
  const isMobile = useMediaQuery('mobile');

  const handleCheckboxChange = (
    formik: FormikProps<IReportOrderItemsFormData>,
    item: IReportOrderItemsItemRefund,
    event: any
  ) => {
    formik.setFieldValue(
      'items',
      deepUpdate(formik.values.items, item, {
        refundCount: event.target.checked ? item.quantity : 0,
      })
    );
  };

  const handleRefundCountInputChange = (
    formik: FormikProps<IReportOrderItemsFormData>,
    item: IReportOrderItemsItemRefund,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    formik.setFieldValue(
      'items',
      deepUpdate(formik.values.items, item, {
        refundCount: parseInt(event.target.value),
      })
    );
  };

  const handleRefundCountInputBlur = (
    formik: FormikProps<IReportOrderItemsFormData>,
    item: IReportOrderItemsItemRefund,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.value === '') {
      formik.setFieldValue(
        'items',
        deepUpdate(formik.values.items, item, {
          refundCount: 0,
        })
      );
    }
  };

  const handleFormikSubmit = (values: IReportOrderItemsFormData) => onSubmit(values);

  return (
    <PageWrap>
      <PageHeading as="h1">{headingText}</PageHeading>
      <StyledBackButton onClick={onBack} />
      <Spacing x={2} y={1}>
        <CenteredP>
          <strong>{subheadingText}</strong>
          <br />
          {contentText}
        </CenteredP>
      </Spacing>
      {!items.length ? (
        <LoadingAnimation fillColor={Styles.color.primary} />
      ) : (
        <Formik<IReportOrderItemsFormData>
          initialValues={{
            comments: '',
            items: mapItemsToRefundItems(items) || [],
          }}
          onSubmit={handleFormikSubmit}
          validateOnMount
          validate={values => {
            const areSomeItemsSelected = values.items.some(isItemOrChildSelected);
            setIsNothingSelected(!areSomeItemsSelected);
          }}
        >
          {formikProps => (
            <form onSubmit={formikProps.handleSubmit}>
              <List data-testid="report-items-missing-items">
                {formikProps.values.items.map(item => (
                  <ListItem key={item.id} data-testid="report-items-missing-item">
                    <Spacing x={6} y={1}>
                      <LeftMiddleFlex>
                        {!item.children?.length && (
                          <>
                            <Checkbox
                              name="confirm missing items"
                              label={null}
                              checked={item.refundCount > 0}
                              onChange={e => handleCheckboxChange(formikProps, item, e)}
                              data-testid="report-items-missing-item-checkbox"
                            />
                            <MissingItemNumberInput
                              max={item.quantity}
                              value={item.refundCount}
                              onBlur={e => handleRefundCountInputBlur(formikProps, item, e)}
                              onChange={e => handleRefundCountInputChange(formikProps, item, e)}
                              data-testid="report-items-missing-count"
                              isContentWidth
                            />
                          </>
                        )}
                        <ItemNameText data-testid="report-items-missing-item-text">
                          {item.quantity}x {item.name}
                        </ItemNameText>
                      </LeftMiddleFlex>
                    </Spacing>
                    {item.children?.map(child => (
                      <Spacing
                        key={child.id}
                        x={isMobile ? 5 : 8}
                        y={1}
                        data-testid="report-items-missing-item"
                      >
                        <LeftMiddleFlex>
                          <Checkbox
                            name="confirm missing items"
                            label={null}
                            checked={child.refundCount > 0}
                            onChange={e => handleCheckboxChange(formikProps, child, e)}
                            data-testid="report-items-missing-item-checkbox"
                          />
                          <MissingItemNumberInput
                            max={child.quantity}
                            value={child.refundCount}
                            onBlur={e => handleRefundCountInputBlur(formikProps, child, e)}
                            onChange={e => handleRefundCountInputChange(formikProps, child, e)}
                            data-testid="report-items-missing-count"
                            isContentWidth
                          />

                          <ItemNameText data-testid="report-items-missing-item-text">
                            {child.quantity}x {child.name}
                          </ItemNameText>
                        </LeftMiddleFlex>
                      </Spacing>
                    ))}
                  </ListItem>
                ))}
              </List>
              <Spacing x={4}>
                <Spacing y={1}>
                  <CommentTextareaInput
                    name="comments"
                    value={formikProps.values.comments}
                    onChange={formikProps.handleChange}
                    placeholder={`${commentsPlaceholderText}:`}
                    data-testid="report-items-missing-comments-field"
                  />
                </Spacing>
                <Spacing y={1}>
                  <Box width="100%" center>
                    <ActionButton
                      type="submit"
                      disabled={
                        disableSubmitButton || formikProps.isSubmitting || isNothingSelected
                      }
                      data-testid="report-items-missing-submit"
                      isLoading={isLoading}
                    >
                      {submitButtonText}
                    </ActionButton>
                  </Box>
                </Spacing>
                {formStatus === FormStatus.SUCCESSFUL_REFUND && (
                  <SupportAlertBox alertStyle={ALERT_STYLE.success} testId="report-order-success">
                    {refundSuccessText}
                  </SupportAlertBox>
                )}
                {formStatus === FormStatus.FAILED_REFUND && (
                  <SupportAlertBox alertStyle={ALERT_STYLE.error} testId="report-order-failed">
                    {refundErrorText}
                  </SupportAlertBox>
                )}
              </Spacing>
            </form>
          )}
        </Formik>
      )}
    </PageWrap>
  );
};

export default ReportOrderItemsMissingPageView;
